import { useMemo, useState } from 'react';
import { Box, Typography, Chip, Accordion, AccordionSummary, AccordionDetails, LinearProgress, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { formatCurrency, formatDate } from '../../utils/helpers';
import SectionTitle from '../Common/Title';
import { FaChevronDown, FaChartLine, FaHistory, FaCalendarAlt, FaClock } from 'react-icons/fa';

const CustomerStatistics = ({ orders = [], customer }) => {
  const [expanded, setExpanded] = useState(false);

  // Calcul des métriques de base et avancées
  const stats = useMemo(() => {
    if (!orders || orders.length === 0) {
      return {
        totalSpent: 0,
        orderCount: 0,
        averageOrderValue: 0,
        favoriteItem: null,
        lastOrderDate: null,
        preferredDays: [],
        preferredTimes: [],
        spendingTrend: [],
        visitFrequency: 0,
        topCategories: [],
        daysActive: 0,
        recency: null,
        frequencyDistribution: null
      };
    }

    // Tri des commandes par date (de la plus récente à la plus ancienne)
    const sortedOrders = [...orders].sort((a, b) => {
      const dateA = a.pickupTime?.toDate?.() || new Date(a.pickupTime);
      const dateB = b.pickupTime?.toDate?.() || new Date(b.pickupTime);
      return dateB - dateA;
    });

    // Montant total dépensé
    const totalSpent = orders.reduce((total, order) => total + (order.totalPrice ?? 0), 0);
    
    // Panier moyen
    const averageOrderValue = totalSpent / orders.length;
    
    // Date de la dernière commande
    const lastOrderDate = sortedOrders.length > 0 ? sortedOrders[0].pickupTime : null;
    
    // Article préféré (celui commandé le plus fréquemment)
    const itemCounts = {};
    const categoryCount = {};
    let favoriteItem = null;
    let maxCount = 0;
    
    orders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          // Compter selon l'ID ou le nom de l'item
          const itemId = item.id || item.name;
          if (!itemId) return;
          
          const quantity = item.quantity || 1;
          itemCounts[itemId] = (itemCounts[itemId] || 0) + quantity;
          
          // Compter les catégories
          if (item.category) {
            categoryCount[item.category] = (categoryCount[item.category] || 0) + quantity;
          }
          
          // Mettre à jour l'article préféré si nécessaire
          if (itemCounts[itemId] > maxCount) {
            maxCount = itemCounts[itemId];
            favoriteItem = item;
          }
        });
      }
    });
    
    // Jours et heures préférés
    const dayCount = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
    const timeCount = { morning: 0, noon: 0, afternoon: 0, evening: 0, night: 0 };
    const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    
    orders.forEach(order => {
      const date = order.pickupTime?.toDate?.() || new Date(order.pickupTime);
      
      // Jour de la semaine
      const day = date.getDay();
      dayCount[day] = (dayCount[day] || 0) + 1;
      
      // Moment de la journée
      const hour = date.getHours();
      if (hour >= 6 && hour < 11) timeCount.morning++;
      else if (hour >= 11 && hour < 14) timeCount.noon++;
      else if (hour >= 14 && hour < 18) timeCount.afternoon++;
      else if (hour >= 18 && hour < 22) timeCount.evening++;
      else timeCount.night++;
    });
    
    // Trouver le jour préféré
    const preferredDays = Object.entries(dayCount)
      .sort((a, b) => b[1] - a[1])
      .filter((day, idx, self) => day[1] > 0 && (idx === 0 || day[1] === self[0][1]))
      .map(day => ({ day: parseInt(day[0]), name: dayNames[day[0]], count: day[1] }));
    
    // Trouver le moment préféré
    const preferredTimes = Object.entries(timeCount)
      .sort((a, b) => b[1] - a[1])
      .filter((time, idx, self) => time[1] > 0 && (idx === 0 || time[1] === self[0][1]))
      .map(time => ({ time: time[0], count: time[1] }));
    
    // Top 3 des catégories
    const topCategories = Object.entries(categoryCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([name, count]) => ({ name, count }));
    
    // Tendance des dépenses (6 derniers mois)
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    
    const monthlySpending = {};
    orders.forEach(order => {
      const date = order.pickupTime?.toDate?.() || new Date(order.pickupTime);
      if (date < sixMonthsAgo) return;
      
      const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthlySpending[yearMonth] = (monthlySpending[yearMonth] || 0) + (order.totalPrice || 0);
    });
    
    const spendingTrend = Object.entries(monthlySpending)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([month, amount]) => {
        const [year, monthNum] = month.split('-');
        const date = new Date(parseInt(year), parseInt(monthNum) - 1);
        return {
          month: date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
          amount
        };
      });
    
    // Calcul de la fréquence de visite (en jours)
    let visitFrequency = 0;
    if (orders.length > 1) {
      const firstDate = sortedOrders[sortedOrders.length - 1].pickupTime?.toDate?.() || new Date(sortedOrders[sortedOrders.length - 1].pickupTime);
      const lastDate = sortedOrders[0].pickupTime?.toDate?.() || new Date(sortedOrders[0].pickupTime);
      const daysBetween = Math.ceil((lastDate - firstDate) / (1000 * 60 * 60 * 24));
      visitFrequency = daysBetween / (orders.length - 1);
    }
    
    // Nombre de jours depuis la première commande
    const firstOrderDate = sortedOrders[sortedOrders.length - 1].pickupTime?.toDate?.() || new Date(sortedOrders[sortedOrders.length - 1].pickupTime);
    const daysActive = Math.ceil((new Date() - firstOrderDate) / (1000 * 60 * 60 * 24));
    
    // Nombre de jours depuis la dernière commande (récence)
    const lastDate = sortedOrders[0].pickupTime?.toDate?.() || new Date(sortedOrders[0].pickupTime);
    const recency = Math.ceil((new Date() - lastDate) / (1000 * 60 * 60 * 24));
    
    // Distribution RFM (Récence, Fréquence, Montant)
    const frequencyDistribution = {
      recency: recency < 30 ? 'élevé' : recency < 90 ? 'moyen' : 'faible',
      frequency: orders.length > 10 ? 'élevé' : orders.length > 3 ? 'moyen' : 'faible',
      monetary: averageOrderValue > 30 ? 'élevé' : averageOrderValue > 15 ? 'moyen' : 'faible'
    };
    
    return {
      totalSpent,
      orderCount: orders.length,
      averageOrderValue,
      favoriteItem,
      lastOrderDate,
      preferredDays,
      preferredTimes,
      spendingTrend,
      visitFrequency,
      topCategories,
      daysActive,
      itemCounts,
      recency,
      frequencyDistribution,
      firstOrderDate
    };
  }, [orders]);

  // Fonction pour formater un nombre de jours en texte
  const formatDays = (days) => {
    if (days < 1) return "aujourd'hui";
    if (days === 1) return "hier";
    if (days < 30) return `il y a ${days} jours`;
    if (days < 365) {
      const months = Math.floor(days / 30);
      return `il y a ${months} mois`;
    }
    const years = Math.floor(days / 365);
    return `il y a ${years} an${years > 1 ? 's' : ''}`;
  };

  // Fonction pour obtenir la couleur d'une métrique RFM
  const getRFMColor = (value) => {
    switch (value) {
      case 'élevé': return 'success.main';
      case 'moyen': return 'warning.main';
      case 'faible': return 'error.main';
      default: return 'text.primary';
    }
  };

  // Fonction pour calculer si le taux de conversion est bon
  const getConversionRate = () => {
    if (!customer?.viewCount || customer.viewCount === 0) return null;
    return {
      rate: (stats.orderCount / customer.viewCount) * 100,
      viewCount: customer.viewCount
    };
  };

  const conversionRate = getConversionRate();

  return (
    <Box sx={{ mb: 4 }}>
      <SectionTitle>Statistiques des commandes</SectionTitle>
      
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
        <Typography variant="body1">
          Nombre de commandes : <strong>{stats.orderCount}</strong>
        </Typography>
        <Typography variant="body1">
          Total dépensé : <strong>{formatCurrency(stats.totalSpent)}</strong>
        </Typography>
        <Typography variant="body1">
          Panier moyen : <strong>{formatCurrency(stats.averageOrderValue)}</strong>
        </Typography>
        <Typography variant="body1">
          Dernière commande : <strong>
            {stats.lastOrderDate ? formatDate(stats.lastOrderDate) : "Aucune"}
          </strong>
        </Typography>
      </Box>
      
      {stats.favoriteItem && (
        <Box sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Article préféré :
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Chip 
              label={stats.favoriteItem.name} 
              color="primary" 
              variant="outlined"
              sx={{ fontWeight: 'bold' }}
            />
            <Typography variant="body2" color="text.secondary">
              Commandé {stats.itemCounts[stats.favoriteItem.id || stats.favoriteItem.name]} fois
            </Typography>
          </Box>
        </Box>
      )}
      
      {stats.orderCount === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
          Ce client n'a pas encore passé de commande.
        </Typography>
      ) : (
        <Accordion 
          expanded={expanded} 
          onChange={() => setExpanded(!expanded)}
          sx={{ mt: 2, mb: 2 }}
        >
          <AccordionSummary expandIcon={<FaChevronDown />}>
            <Typography sx={{ fontWeight: 'medium' }}>
              Statistiques avancées
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Fidélité et récence */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FaHistory /> Historique client
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2">
                    Client depuis: <strong>{formatDate(stats.firstOrderDate)}</strong> ({stats.daysActive} jours)
                  </Typography>
                  <Typography variant="body2">
                    Dernière visite: <strong>{formatDays(stats.recency)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Fréquence moyenne: tous les <strong>{Math.round(stats.visitFrequency)} jours</strong>
                  </Typography>
                </Box>
              </Box>
              
              {/* Profil de score client */}
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FaChartLine /> Score client (RFM)
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ width: 100 }}>Récence:</Typography>
                    <Tooltip title={`Dernière commande: ${formatDays(stats.recency)}`}>
                      <Box sx={{ color: getRFMColor(stats.frequencyDistribution.recency), fontWeight: 'bold' }}>
                        {stats.frequencyDistribution.recency.toUpperCase()}
                      </Box>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ width: 100 }}>Fréquence:</Typography>
                    <Tooltip title={`${stats.orderCount} commandes au total`}>
                      <Box sx={{ color: getRFMColor(stats.frequencyDistribution.frequency), fontWeight: 'bold' }}>
                        {stats.frequencyDistribution.frequency.toUpperCase()}
                      </Box>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ width: 100 }}>Montant:</Typography>
                    <Tooltip title={`Panier moyen: ${formatCurrency(stats.averageOrderValue)}`}>
                      <Box sx={{ color: getRFMColor(stats.frequencyDistribution.monetary), fontWeight: 'bold' }}>
                        {stats.frequencyDistribution.monetary.toUpperCase()}
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
              
              {/* Préférences journalières */}
              {stats.preferredDays.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <FaCalendarAlt /> Jours préférés
                  </Typography>
                  <Box sx={{ pl: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {stats.preferredDays.map((day, index) => (
                      <Chip 
                        key={index}
                        label={day.name} 
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
              
              {/* Préférences horaires */}
              {stats.preferredTimes.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <FaClock /> Moments préférés
                  </Typography>
                  <Box sx={{ pl: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {stats.preferredTimes.map((time, index) => (
                      <Chip 
                        key={index}
                        label={time.time === 'morning' ? 'Matin' 
                              : time.time === 'noon' ? 'Midi'
                              : time.time === 'afternoon' ? 'Après-midi'
                              : time.time === 'evening' ? 'Soir'
                              : 'Nuit'} 
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
              
              {/* Taux de conversion */}
              {conversionRate && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Taux de conversion
                  </Typography>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      {conversionRate.rate.toFixed(1)}% ({stats.orderCount} commandes / {conversionRate.viewCount} visites)
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(100, conversionRate.rate)} 
                      sx={{ 
                        height: 8, 
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: conversionRate.rate > 20 ? 'success.main' : 
                                  conversionRate.rate > 10 ? 'warning.main' : 'error.main'
                        }
                      }}
                    />
                  </Box>
                </Box>
              )}
              
              {/* Tableau des dépenses au lieu du graphique */}
              {stats.spendingTrend.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Évolution des dépenses (6 derniers mois)
                  </Typography>
                  <TableContainer sx={{ maxHeight: 200 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Mois</TableCell>
                          <TableCell align="right">Montant</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.spendingTrend.map((entry, index) => (
                          <TableRow key={index}>
                            <TableCell component="th" scope="row">{entry.month}</TableCell>
                            <TableCell align="right">{formatCurrency(entry.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default CustomerStatistics;